import { useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import { submitStory } from "../lib/api";
import { toast } from "sonner";

const ROLES = [
  { value: "parent", label: "A parent" },
  { value: "grandparent", label: "A grandparent" },
  { value: "kinship_carer", label: "A kinship carer (aunt, uncle, family friend…)" },
  { value: "other_family", label: "Another family member" },
  { value: "prefer_not_to_say", label: "I'd rather not say" },
];

const EMPTY = {
  pen_name: "",
  title: "",
  story: "",
  role: "parent",
  region: "",
  publish_consent: false,
};

const LABEL_CLS = "block text-xs uppercase tracking-[0.18em] font-accent font-semibold text-ink mb-2";
const INPUT_CLS = "w-full border border-rule bg-linen px-4 py-3 text-base outline-none focus:border-river";

function StorySidebar() {
  return (
    <aside className="lg:col-span-4 space-y-8">
      <div className="border-l-2 border-ember pl-5">
        <h2 className="font-serif text-2xl text-ink leading-tight tracking-tight">
          Before you write
        </h2>
        <p className="mt-3 text-ink-muted leading-relaxed">
          Take your time. There is no right length, no right shape, no right ending. Your story is your own.
        </p>
      </div>
      <div className="space-y-4 text-ink-muted leading-relaxed">
        <div>
          <strong className="text-ink font-serif text-lg block">Anonymity is standard</strong>
          Use any name you like as your pen name. Nothing personally identifiable will be published without your consent.
        </div>
        <div>
          <strong className="text-ink font-serif text-lg block">Nothing publishes automatically</strong>
          Every submission is read by us. We only publish stories where the submitter has ticked the consent box below.
        </div>
        <div>
          <strong className="text-ink font-serif text-lg block">You can withdraw</strong>
          Change your mind later? Email <a href="mailto:info@truth4parents.com" className="text-river underline">info@truth4parents.com</a> and we will remove your story.
        </div>
        <div>
          <strong className="text-ink font-serif text-lg block">In an emergency</strong>
          This form is not monitored in real time. If a child is in immediate danger, please call <strong>999</strong>. If you need to speak to someone, Samaritans are on <strong>116 123</strong>.
        </div>
      </div>
    </aside>
  );
}

function SubmittedState({ onReset }) {
  return (
    <div
      data-testid="story-share-success"
      className="border border-river bg-white p-10 md:p-14"
    >
      <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
        Thank you
      </div>
      <h2 className="font-serif text-3xl md:text-4xl mt-4 text-ink leading-tight tracking-tight">
        Your story has been received.
      </h2>
      <p className="mt-5 text-ink-muted leading-relaxed">
        We read every submission carefully. Nothing will be published without the consent box being ticked, and we may write back to check details or ask permission for small edits.
      </p>
      <p className="mt-4 text-ink-muted leading-relaxed">
        If you need to reach us in the meantime, <Link to="/contact" className="text-river underline">the contact page</Link> is always open.
      </p>
      <div className="mt-8 flex gap-3">
        <Link
          to="/stories"
          data-testid="story-share-back-link"
          className="inline-flex items-center gap-2 border border-ink/30 text-ink px-6 py-3 text-sm font-accent font-semibold tracking-wide hover:border-ink hover:bg-linen-alt transition-colors"
        >
          Back to stories
        </Link>
        <button
          type="button"
          data-testid="story-share-another-button"
          onClick={onReset}
          className="inline-flex items-center gap-2 text-ink-muted hover:text-ink px-4 py-3 text-sm font-accent font-semibold tracking-wide"
        >
          Submit another
        </button>
      </div>
    </div>
  );
}

export default function StoryShareForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onField = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.publish_consent) {
      toast.error("Please tick the consent box so we know we can publish your story.");
      return;
    }
    setLoading(true);
    try {
      await submitStory({
        pen_name: form.pen_name || "Anonymous",
        title: form.title,
        story: form.story,
        region: form.region || null,
        role: form.role === "prefer_not_to_say" ? null : form.role,
        publish_consent: form.publish_consent,
      });
      setSubmitted(true);
      setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sorry, something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="story-share-page">
      <PageMeta
        title="Share your story"
        description="A private, anonymous form for families to share their experience of the child protection or family court system. Nothing is published without your consent."
        path="/stories/share"
      />
      <PageHeader
        overline="Share your story"
        title="In your own words."
        lede="If your story could help another family understand what to expect, or feel less alone, we would be honoured to hear it."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <StorySidebar />
          <div className="lg:col-span-8">
            {submitted ? (
              <SubmittedState onReset={() => setSubmitted(false)} />
            ) : (
              <form
                data-testid="story-share-form"
                onSubmit={onSubmit}
                className="space-y-7 border border-rule bg-white p-8 md:p-12"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="story-pen" className={LABEL_CLS}>
                      Pen name <span className="ml-2 text-ink-muted font-normal normal-case tracking-normal">(optional)</span>
                    </label>
                    <input
                      id="story-pen"
                      data-testid="story-pen-input"
                      type="text"
                      value={form.pen_name}
                      onChange={onField("pen_name")}
                      maxLength={80}
                      className={INPUT_CLS}
                      placeholder="Sarah, Yorkshire — or leave blank for Anonymous"
                    />
                  </div>
                  <div>
                    <label htmlFor="story-role" className={LABEL_CLS}>
                      You are…
                    </label>
                    <select
                      id="story-role"
                      data-testid="story-role-select"
                      value={form.role}
                      onChange={onField("role")}
                      className={INPUT_CLS}
                    >
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="story-title" className={LABEL_CLS}>
                    A title for your story
                  </label>
                  <input
                    id="story-title"
                    data-testid="story-title-input"
                    type="text"
                    required
                    minLength={2}
                    maxLength={200}
                    value={form.title}
                    onChange={onField("title")}
                    className={INPUT_CLS}
                    placeholder="One line, in your own voice"
                  />
                </div>

                <div>
                  <label htmlFor="story-region" className={LABEL_CLS}>
                    Region <span className="ml-2 text-ink-muted font-normal normal-case tracking-normal">(optional)</span>
                  </label>
                  <input
                    id="story-region"
                    data-testid="story-region-input"
                    type="text"
                    value={form.region}
                    onChange={onField("region")}
                    maxLength={80}
                    className={INPUT_CLS}
                    placeholder="e.g. West Yorkshire, South Wales"
                  />
                </div>

                <div>
                  <label htmlFor="story-body" className={LABEL_CLS}>
                    Your story
                  </label>
                  <textarea
                    id="story-body"
                    data-testid="story-body-textarea"
                    required
                    minLength={20}
                    maxLength={10000}
                    value={form.story}
                    onChange={onField("story")}
                    rows={14}
                    className={`${INPUT_CLS} resize-y`}
                    placeholder="Write as much or as little as feels right. There is no correct shape for this."
                  />
                  <div className="mt-2 flex items-center justify-between text-xs text-ink-muted font-accent">
                    <span>Minimum 20 characters. No maximum in practice.</span>
                    <span>{form.story.length} / 10,000</span>
                  </div>
                </div>

                <label className="flex items-start gap-3 text-sm text-ink-muted leading-relaxed cursor-pointer border-t border-rule pt-6">
                  <input
                    type="checkbox"
                    data-testid="story-consent-checkbox"
                    checked={form.publish_consent}
                    onChange={onField("publish_consent")}
                    className="mt-1 flex-shrink-0 w-4 h-4 accent-river"
                  />
                  <span>
                    I confirm this is my own story to share and I consent to it being published on this site, either as I wrote it or with small edits for clarity. I understand I can withdraw my consent at any time by emailing <a href="mailto:info@truth4parents.com" className="text-river underline">info@truth4parents.com</a>.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="story-submit-button"
                  className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-river text-linen px-8 py-4 text-sm font-accent font-semibold tracking-wide hover:bg-river-dark transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Share my story"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
